import { useRouter } from 'next/router';
import React, { useCallback, useContext, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import cx from 'classnames';
import chunk from 'lodash/chunk';
import { DonateNowFormSteps, DonateNowFormStep } from 'utils/checkInterface';
import processGoogleTag from 'utils/processGoogleTag';
import { scrollToElement } from 'utils/scrollTo';

import CampaignContentsSection from 'components/CampaignContentsSection/CampaignContentsSection';
import Card from 'components/Card/Card';
import FAQAccordionList from 'components/ContentRecord/components/FAQAccordionList/FAQAccordionList';
import DonateNowForm from 'components/DonateNowForm/DonateNowForm';
import DonateTodayWithSurfLifeSaverAvatar from 'components/DonateTodayWithSurfLifeSaverAvatar/DonateTodayWithSurfLifeSaverAvatar';
import DonationCampaignBannerSection from 'components/DonationCampaignBannerSection/DonationCampaignBannerSection';
import DynamicHeadingLine from 'components/DynamicHeadingLine/DynamicHeadingLine';
import DynamicSlider from 'components/DynamicSlider/DynamicSlider';
import HorizontalRoundedCornerContent from 'components/HorizontalRoundedCornerContent/HorizontalRoundedCornerContent';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import RowWithOffsetCol from 'components/RowWithOffsetCol/RowWithOffsetCol';
import SimpleDonateForm, {
  ISimpleDonateForm,
} from 'components/SimpleDonateForm/SimpleDonateForm';
import SLSFProgressBar from 'components/SLSFProgressBar/SLSFProgressBar';
import { CartContext } from 'contexts/cartContext';
import { IDonationCampaign } from 'models/donationCampaign';
import { Frequency } from 'models/frequency';
import { ISalesOrderItem } from 'models/salesOrderItem';
import { IThankYouMessage } from 'models/thankYouMessage';

import s from './RegularDonationCampaignPage.module.scss';

const SLIDER_SETTINGS = {
  dots: false,
  infinite: true,
  speed: 500,
  autoplaySpeed: 5000,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  autoplay: true,
};

interface RegularDonationCampaignPageProps {
  donationCampaign: IDonationCampaign;
  messages: IThankYouMessage[];
  donations: ISalesOrderItem[];
  donateNowRef: React.RefObject<HTMLDivElement>;
}
const RegularDonationCampaignPage: React.FC<
  RegularDonationCampaignPageProps
> = ({ donationCampaign, messages, donations, donateNowRef }) => {
  const router = useRouter();
  const {
    actions: { goToCheckoutPage },
  } = useContext(CartContext);
  const [donateLoading, setDonateLoading] = useState(false);
  const [step, setStep] = useState<DonateNowFormStep>(
    DonateNowFormSteps.selectDonation,
  );
  const [simpleDonateFormValue, setSimpleDonateFormValue] =
    useState<ISimpleDonateForm>({
      frequency: Frequency.Monthly,
      donationDay: 28,
    });

  const [donateNowForm, setDonateNow] = useState<ISimpleDonateForm>({
    frequency: Frequency.OnceOff,
  });

  const handlePurchase = useCallback(
    async (
      value: any,
      asAGuest: boolean,
      createNewAccountTabActive: boolean,
    ) => {
      setDonateLoading(true);
      await goToCheckoutPage(
        {
          donation_campaign_id: donationCampaign?.id,
          donation_option_id: value?.donationOption?.id,
          donation_day: value.donationDay,
          frequency: value.frequency,
          custom_unit_amount: value.customAmount,
          quantity: 1,
        },
        asAGuest,
        createNewAccountTabActive,
      );
    },
    [donationCampaign?.id, goToCheckoutPage],
  );

  const handleOnDonateNowChange = useCallback(
    (wholeValue: any, key: string, value: any) => {
      setDonateNow(wholeValue);
      const currentSlug = router.asPath;
      let message = '';
      let finalValue = value;
      const category = currentSlug;
      const action = 'Donate';
      switch (key) {
        case 'donationOption':
          message = 'Donation option selected';
          finalValue = value.amount;
          break;
        case 'customAmount':
          message = 'Donation custom amount selected';
          break;
        case 'donationDay':
          message = 'Donation day selected';
          break;
        case 'frequency':
          message = 'Donation frequency selection page';
          break;
        default:
          console.error(`[ERROR]: there is no key ${key}`);
      }
      processGoogleTag({
        event: 'choose donation',
        slug: category,
        action,
        message,
        value: finalValue,
      });
    },
    [router.asPath],
  );

  const handleSimpleDonateChange = useCallback((value: ISimpleDonateForm) => {
    setSimpleDonateFormValue(value);
  }, []);

  const handlePurchaseSimpleDonate = useCallback(
    (value: ISimpleDonateForm) => {
      if (!donateNowRef?.current) {
        return;
      }
      scrollToElement(donateNowRef.current, 'top');
      setDonateNow(value);
      setStep(DonateNowFormSteps.summary);
    },
    [donateNowRef],
  );

  const handleStepChange = useCallback((nextStep: DonateNowFormStep) => {
    setStep(nextStep);
  }, []);

  return (
    <>
      <DonationCampaignBannerSection
        backgroundImageURL={donationCampaign.banner_image?.url}
        backgroundImageMobileURL={donationCampaign.mobile_banner_image?.url}
        bannerImageAlt={donationCampaign.banner_image_alt}
      >
        <Row>
          <div
            className={cx(
              'col-xl-6 col-lg-5 px-5 px-sm-2 px-md-3 pt-3 pb-2 pt-lg-9 pb-lg-8',
              s['banner-background-only-sm'],
            )}
          >
            {donationCampaign.heading && !donationCampaign.heading_image && (
              <h1
                className={cx(
                  'heading',
                  'heading-secondary',
                  'text-white',
                  s['heading-bold'],
                )}
              >
                {donationCampaign.heading}
              </h1>
            )}
            {donationCampaign.heading && donationCampaign.heading_image && (
              <img
                className="w-75 mb-4 mt-0 mt-md-5 mt-lg-0"
                src={donationCampaign.heading_image.url}
                alt={donationCampaign.heading_image_alt}
              />
            )}
            {donationCampaign.sub_heading && (
              <h2 className={cx('mb-4', 'text-white', s['sub-heading-bold'])}>
                {donationCampaign.sub_heading}
              </h2>
            )}
            {donationCampaign.goal_amount &&
              Number(donationCampaign.goal_amount) > 0 && (
                <SLSFProgressBar
                  amountAchieved={donationCampaign.raised_amount as number}
                  amountToAchieve={donationCampaign.goal_amount as number}
                />
              )}
          </div>

          <Col lg={{ span: 6, offset: 1 }} xl={5} className={s['donate-card']}>
            <div ref={donateNowRef} className={s['donate-now-form-wrapper']}>
              {!!donationCampaign.donation_options?.length && (
                <DonateNowForm
                  step={step}
                  loading={donateLoading}
                  className={s['donate-now-form']}
                  cardProps={{
                    className: s['donate-now-form-card'],
                  }}
                  onPurchase={async (
                    value,
                    asAGuest,
                    createNewAccountTabActive,
                  ) => {
                    await handlePurchase(
                      value,
                      asAGuest,
                      createNewAccountTabActive,
                    );
                  }}
                  value={donateNowForm}
                  onChange={handleOnDonateNowChange}
                  donationOptions={donationCampaign.donation_options}
                  onStepChange={handleStepChange}
                />
              )}
            </div>
          </Col>
        </Row>
      </DonationCampaignBannerSection>
      {donationCampaign.content_brief && (
        <SectionWithContainer
          style={{
            backgroundColor: donationCampaign.colour,
          }}
          className={cx(s['campaign-content-brief'], 'py-5')}
        >
          <Row>
            <Col lg={6} xl={7}>
              <div
                className={cx(
                  s['campaign-secondary-text'],
                  'text-white',
                  'font-weight-bold',
                )}
                data-content="donation_campaign_content_brief"
                dangerouslySetInnerHTML={{
                  __html: donationCampaign.content_brief,
                }}
              />
            </Col>
          </Row>
        </SectionWithContainer>
      )}
      {!!messages.length && (
        <SectionWithContainer className="bg-light-grey">
          <RowWithOffsetCol>
            <h1 className="mb-5">
              <span>A wave of support for your lifesavers.</span>
              <DynamicHeadingLine
                colour={donationCampaign.colour}
                className="my-2"
              />
            </h1>
          </RowWithOffsetCol>
          <Row>
            <Col className="mb-3 d-flex" lg={6}>
              <Card className="no-shadow w-100">
                <Card.Body>
                  <DynamicSlider
                    className={s['main-slick-slider']}
                    {...SLIDER_SETTINGS}
                  >
                    {chunk<ISalesOrderItem>(donations, 3).map(donationChunk => (
                      <div key={donationChunk.map(({ id }) => id).join('-')}>
                        {donationChunk.map((donation: ISalesOrderItem) => (
                          <div key={donation.id} className="my-2">
                            <h3 className="text-primary mb-0">
                              {donation.sales_order?.customer?.first_name}{' '}
                              donated $
                              {parseInt(donation.total_item_amount || '0', 10)}
                            </h3>
                            <p>
                              to {donation.donation_campaign?.campaign_name}.
                              Thank-you!
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </DynamicSlider>
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3 d-flex" lg={6}>
              <Card className="no-shadow w-100">
                <Card.Body>
                  <DynamicSlider
                    className={s['main-slick-slider']}
                    {...SLIDER_SETTINGS}
                    fade
                    autoplaySpeed={4500}
                  >
                    {chunk<IThankYouMessage>(messages, 2).map(
                      thankYouMessageChunk => (
                        <div
                          key={thankYouMessageChunk
                            .map(({ id }) => id)
                            .join('-')}
                        >
                          {thankYouMessageChunk.map(
                            (thankYouMessage: IThankYouMessage) => (
                              <div key={thankYouMessage.id} className="my-2">
                                <h3 className="text-primary mb-2">
                                  {thankYouMessage?.customer?.first_name} says
                                </h3>
                                <p>"{thankYouMessage.message}"</p>
                              </div>
                            ),
                          )}
                        </div>
                      ),
                    )}
                  </DynamicSlider>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <RowWithOffsetCol className="mt-4" offset={3}>
            <DonateTodayWithSurfLifeSaverAvatar
              onDonateToday={() => {
                if (!donateNowRef.current) {
                  return;
                }
                scrollToElement(donateNowRef.current, 'top');
              }}
            />
          </RowWithOffsetCol>
        </SectionWithContainer>
      )}
      {!!donationCampaign.campaign_contents?.length && (
        <CampaignContentsSection
          campaignContents={donationCampaign.campaign_contents}
          colour={donationCampaign.colour}
        />
      )}
      {!!donationCampaign.featured_faqs?.length && (
        <SectionWithContainer>
          <Row className="mb-5">
            <Col lg={7}>
              <h2 className="heading heading-light-blue">
                Where does my money go? Can even a small donation help? Your
                questions answered.
              </h2>
            </Col>
          </Row>
          <RowWithOffsetCol>
            <FAQAccordionList faqs={donationCampaign.featured_faqs} />
          </RowWithOffsetCol>
        </SectionWithContainer>
      )}
      {!!donationCampaign.donation_options?.length && (
        <SectionWithContainer>
          <HorizontalRoundedCornerContent>
            <HorizontalRoundedCornerContent.Col className="bg-primary text-white">
              <h2 className="heading heading-secondary mb-3 w-100">
                Could you give regularly?
              </h2>
              <p className="w-100">
                As well as training new volunteer surf lifesaver, your monthly
                donation helps repair and replace the equipment that constant
                exposure to the sun and sea can damage. As a Guardian of the
                Surf your monthly donation ensures that we are rescue-ready at a
                moment’s notice. You would be the silent hero behind every life
                saved.
              </p>
              <h5>Join our Guardians of the Surf family today</h5>
            </HorizontalRoundedCornerContent.Col>
            <HorizontalRoundedCornerContent.Col className="bg-secondary">
              <SimpleDonateForm
                donationOptions={donationCampaign.donation_options}
                onChange={handleSimpleDonateChange}
                onPurchase={handlePurchaseSimpleDonate}
                value={simpleDonateFormValue}
              />
            </HorizontalRoundedCornerContent.Col>
          </HorizontalRoundedCornerContent>
        </SectionWithContainer>
      )}
    </>
  );
};

export default RegularDonationCampaignPage;
